// SizeChartGridEditor — the `grid` field of a StoreSizeChart: a table of
// measurements (rows) by sizes (columns). The value is
// { rows: [measurement], cols: [size], cells: [[text]] }, cells rows × cols.
//
// Add or remove a size or a measurement, type into any cell; a block copied
// from a spreadsheet (tab-separated, the sizes in its first line, each next
// line a measurement and its values) can be pasted to replace the table.
import React, { useState } from "react"
import { PlusIcon, XMarkIcon, ClipboardDocumentIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"

const str = (value) => (value === undefined || value === null ? "" : String(value))

/** The grid made whole: string rows and cols, cells rows × cols. */
export const normalizeGrid = (value) => {
  const rows = Array.isArray(value?.rows) ? value.rows.map(str) : []
  const cols = Array.isArray(value?.cols) ? value.cols.map(str) : []
  const cells = rows.map((_, r) => cols.map((__, c) => str(value?.cells?.[r]?.[c])))
  return { rows, cols, cells }
}

/** Tab-separated text (a spreadsheet's copy) as a grid; null when it is not one. */
export const gridFromText = (text) => {
  const lines = String(text || "").split(/\r?\n/).filter((line) => line.trim())
  if (lines.length < 2) return null
  const split = (line) => line.split("\t").map((cell) => cell.trim())
  const [, ...cols] = split(lines[0])
  if (!cols.length) return null
  const body = lines.slice(1).map(split)
  return { rows: body.map((line) => line[0] || ""), cols, cells: body.map((line) => cols.map((_, c) => line[c + 1] || "")) }
}

const input = "w-full min-w-[72px] h-9 px-2 rounded border border-admin_bordered bg-admin_paper text-admin_text text-sm outline-none focus:border-admin_primary disabled:opacity-60"

export default function SizeChartGridEditor({ value, onChange, readOnly = false }) {
  const grid = normalizeGrid(value)
  const [pasting, setPasting] = useState(false)
  const [pasted, setPasted] = useState("")
  const emit = (next) => !readOnly && onChange?.(normalizeGrid(next))

  const setCol = (c, text) => emit({ ...grid, cols: grid.cols.map((col, i) => (i === c ? text : col)) })
  const setRow = (r, text) => emit({ ...grid, rows: grid.rows.map((row, i) => (i === r ? text : row)) })
  const setCell = (r, c, text) => emit({ ...grid, cells: grid.cells.map((line, i) => (i === r ? line.map((cell, j) => (j === c ? text : cell)) : line)) })
  const addCol = () => emit({ ...grid, cols: [...grid.cols, ""], cells: grid.cells.map((line) => [...line, ""]) })
  const addRow = () => emit({ ...grid, rows: [...grid.rows, ""], cells: [...grid.cells, grid.cols.map(() => "")] })
  const removeCol = (c) => emit({ ...grid, cols: grid.cols.filter((_, i) => i !== c), cells: grid.cells.map((line) => line.filter((_, i) => i !== c)) })
  const removeRow = (r) => emit({ ...grid, rows: grid.rows.filter((_, i) => i !== r), cells: grid.cells.filter((_, i) => i !== r) })

  const applyPaste = () => {
    const next = gridFromText(pasted)
    if (!next) return
    emit(next)
    setPasted("")
    setPasting(false)
  }

  const remove = (onClick, label) =>
    !readOnly && (
      <button type="button" onClick={onClick} className="p-1 rounded text-admin_text_secondary hover:text-admin_error shrink-0" title={label} aria-label={label}>
        <XMarkIcon className="w-4 h-4" />
      </button>
    )

  return (
    <div className="flex flex-col gap-3 w-full" data-testid="size-chart-grid">
      <div className="w-full overflow-x-auto border border-admin_bordered rounded-lg">
        <table className="border-collapse text-sm">
          <thead>
            <tr>
              <th className="p-2 text-left text-admin_text_secondary font-medium whitespace-nowrap">Measurement \ Size</th>
              {grid.cols.map((col, c) => (
                <th key={c} className="p-1">
                  <div className="flex items-center gap-1">
                    <input className={input} value={col} placeholder="Size" disabled={readOnly} onChange={(e) => setCol(c, e.target.value)} data-testid="size-chart-col" />
                    {remove(() => removeCol(c), "Remove size")}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grid.rows.map((row, r) => (
              <tr key={r}>
                <td className="p-1">
                  <div className="flex items-center gap-1">
                    {remove(() => removeRow(r), "Remove measurement")}
                    <input className={input} value={row} placeholder="Measurement" disabled={readOnly} onChange={(e) => setRow(r, e.target.value)} data-testid="size-chart-row" />
                  </div>
                </td>
                {grid.cols.map((_, c) => (
                  <td key={c} className="p-1">
                    <input className={`${input} text-center`} value={grid.cells[r][c]} disabled={readOnly} onChange={(e) => setCell(r, c, e.target.value)} data-testid="size-chart-cell" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {!grid.rows.length && !grid.cols.length && (
          <Typography variant="admin_body2" className="p-3 opacity-70">
            Empty: add the sizes and the measurements, or paste a table.
          </Typography>
        )}
      </div>

      {!readOnly && (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="admin_secondary" onClick={addCol} data-testid="size-chart-add-col">
            <PlusIcon className="w-4 h-4 mr-1 inline" />
            Size
          </Button>
          <Button variant="admin_secondary" onClick={addRow} data-testid="size-chart-add-row">
            <PlusIcon className="w-4 h-4 mr-1 inline" />
            Measurement
          </Button>
          <Button variant="admin_secondary" onClick={() => setPasting((on) => !on)}>
            <ClipboardDocumentIcon className="w-4 h-4 mr-1 inline" />
            Paste a table
          </Button>
        </div>
      )}

      {pasting && !readOnly && (
        <div className="flex flex-col gap-2">
          <textarea
            value={pasted}
            onChange={(e) => setPasted(e.target.value)}
            rows={5}
            placeholder={"Copied from a spreadsheet: the sizes in the first line, then one measurement a line\n\tS\tM\tL\nChest\t50\t52\t54"}
            className="w-full p-2 rounded border border-admin_bordered bg-admin_paper text-admin_text text-sm font-mono outline-none focus:border-admin_primary"
          />
          <div>
            <Button variant="admin_primary" disabled={!gridFromText(pasted)} onClick={applyPaste}>
              Replace the table
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
